/**
 * batch-62 kostprijs-werkelijk (FO-460 + FO-461) — pure regels achter de werkelijke
 * kostprijs. I/O-vrij: de API-laag laadt de data (batch, geen N+1) en roept deze
 * functies aan. Geld wordt intern in CENTEN (gehele getallen) opgeteld zodat een
 * regelsom exact is; pas de output gaat terug naar euro's.
 *
 * Onderdelen:
 *   - periodes (jjjjmm, NL-maand) + 4-wekenperiode -> maand;
 *   - kostensoort-classificatie van een MKB-productregel;
 *   - totaalcontrole (som regels + btw = factuurtotaal, anders "te controleren");
 *   - dedupe tegen bestaande tankpas-transacties (tolerantie op dag + bedrag);
 *   - loonmapping: normalisatie, versie-vaste resolutie (EXACT > langste BEVAT),
 *     suggestie uit historie/trefwoord/extractie;
 *   - loonkost-aggregatie per medewerker-periode (runs tellen op, teken blijft);
 *   - bijtelling-toerekening pro rata over de berijder-dagen;
 *   - verbruik (tank-tot-tank) + afwijking t.o.v. het voertuiggemiddelde;
 *   - de samenstelling van de werkelijke kostprijs naast de norm.
 */

import type {
  BijtellingToerekeningStatus,
  BrandstofKostensoort,
  KostprijsLoonCategorie,
  KostprijsMarkering,
  LoonkostBucket,
  LoonMappingKandidaat,
  LoonSuggestieBron,
  WerkgeverslastenBron,
} from '@streep/types'
import {
  BRANDSTOF_KOSTENSOORT,
  BRANDSTOF_LEVERANCIER_PATRONEN,
  KOSTPRIJS_BRUTO_LOON_CATEGORIEEN,
  KOSTPRIJS_TELLENDE_LOON_CATEGORIEEN,
  LOONKOST_BUCKETS,
  LOON_COMPONENT_MAPPING_SEED,
  NOG_TE_CLASSIFICEREN,
} from '@streep/types'
import { amsterdamAllDayStartToUtc, utcToAmsterdamWallclock } from './amsterdam-tijd'

// ─── Geld (centen) ───────────────────────────────────────────────────────────

const naarCenten = (euro: number): number => Math.round(euro * 100)
const naarEuro = (centen: number): number => centen / 100
const rond2 = (n: number): number => Math.round(n * 100) / 100

// ─── Periodes (jjjjmm, NL-maand) ─────────────────────────────────────────────

export function periodeSleutel(jaar: number, maand: number): number {
  return jaar * 100 + maand
}

export function periodeUitSleutel(periode: number): { jaar: number; maand: number } {
  return { jaar: Math.floor(periode / 100), maand: periode % 100 }
}

export function periodeLabel(periode: number): string {
  const { jaar, maand } = periodeUitSleutel(periode)
  return `${jaar}-${String(maand).padStart(2, '0')}`
}

/** `jjjj-mm` -> jjjjmm, of null bij een ongeldige waarde. */
export function parsePeriodeLabel(label: string): number | null {
  const m = /^(\d{4})-(0[1-9]|1[0-2])$/.exec(label.trim())
  if (!m) return null
  return periodeSleutel(Number(m[1]), Number(m[2]))
}

export function volgendePeriode(periode: number): number {
  const { jaar, maand } = periodeUitSleutel(periode)
  return maand === 12 ? periodeSleutel(jaar + 1, 1) : periodeSleutel(jaar, maand + 1)
}

export function vorigePeriode(periode: number): number {
  const { jaar, maand } = periodeUitSleutel(periode)
  return maand === 1 ? periodeSleutel(jaar - 1, 12) : periodeSleutel(jaar, maand - 1)
}

/** Alle periodes van `van` t/m `tot` (inclusief), oplopend. */
export function periodeBereik(van: number, tot: number): number[] {
  const out: number[] = []
  for (let p = van; p <= tot; p = volgendePeriode(p)) out.push(p)
  return out
}

/** De NL-maand waarin een instant valt. */
export function periodeVanInstant(d: Date): number {
  const ymd = utcToAmsterdamWallclock(d).ymd
  return periodeSleutel(Number(ymd.slice(0, 4)), Number(ymd.slice(5, 7)))
}

/** [van, tot) van een periode als UTC-instants (NL-middernacht, DST-veilig). */
export function periodeGrenzenUtc(periode: number): { van: Date; tot: Date } {
  const { jaar, maand } = periodeUitSleutel(periode)
  const volgende = periodeUitSleutel(volgendePeriode(periode))
  return {
    van: amsterdamAllDayStartToUtc(`${jaar}-${String(maand).padStart(2, '0')}-01`),
    tot: amsterdamAllDayStartToUtc(
      `${volgende.jaar}-${String(volgende.maand).padStart(2, '0')}-01`,
    ),
  }
}

/** Aantal kalenderdagen in een periode. */
export function dagenInPeriode(periode: number): number {
  const { jaar, maand } = periodeUitSleutel(periode)
  return new Date(Date.UTC(jaar, maand, 0)).getUTCDate()
}

/**
 * Een 4-wekenperiode (1-13) telt in de NL-maand waarin hij EINDIGT: periode n =
 * ISO-weken 4n-3..4n; het einde is de zondag van ISO-week 4n.
 */
export function vierWekenPeriodeNaarPeriode(jaar: number, periodeNummer: number): number {
  const jan4 = new Date(Date.UTC(jaar, 0, 4))
  const dow = jan4.getUTCDay() === 0 ? 7 : jan4.getUTCDay()
  const maandagWeek1 = Date.UTC(jaar, 0, 4 - (dow - 1))
  const eind = new Date(maandagWeek1 + (periodeNummer * 4 * 7 - 1) * 86_400_000)
  return periodeSleutel(eind.getUTCFullYear(), eind.getUTCMonth() + 1)
}

/** jjjj-mm-dd van een instant in NL-tijd. */
export function nlDag(d: Date): string {
  return utcToAmsterdamWallclock(d).ymd
}

/** Verschil in hele kalenderdagen tussen twee jjjj-mm-dd-strings (b - a). */
export function dagVerschil(a: string, b: string): number {
  const ta = Date.UTC(Number(a.slice(0, 4)), Number(a.slice(5, 7)) - 1, Number(a.slice(8, 10)))
  const tb = Date.UTC(Number(b.slice(0, 4)), Number(b.slice(5, 7)) - 1, Number(b.slice(8, 10)))
  return Math.round((tb - ta) / 86_400_000)
}

// ─── Stabiele hash (consistentie-detectie) ───────────────────────────────────

function sorteerDiep(v: unknown): unknown {
  if (Array.isArray(v)) return v.map(sorteerDiep)
  if (v && typeof v === 'object' && !(v instanceof Date)) {
    const out: Record<string, unknown> = {}
    for (const k of Object.keys(v as Record<string, unknown>).sort()) {
      out[k] = sorteerDiep((v as Record<string, unknown>)[k])
    }
    return out
  }
  if (v instanceof Date) return v.toISOString()
  return v
}

/** Deterministische djb2-hash over een (diep gesorteerde) JSON-waarde. */
export function stabieleHash(waarde: unknown): string {
  const s = JSON.stringify(sorteerDiep(waarde))
  let h = 5381
  for (let i = 0; i < s.length; i++) h = (h * 33) ^ s.charCodeAt(i)
  return (h >>> 0).toString(16)
}

// ─── Brandstof: leverancier + kostensoort ────────────────────────────────────

const normTekst = (s: string): string =>
  s
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, ' ')
    .trim()

/** Is dit (de naam van) een brandstofleverancier (MKB Brandstof of een extra naam)? */
export function isBrandstofLeverancierNaam(
  naam: string | null | undefined,
  extraNamen: readonly string[] = [],
): boolean {
  if (!naam) return false
  const n = normTekst(naam)
  const compact = n.replace(/[^a-z0-9]/g, '')
  const patronen = [...BRANDSTOF_LEVERANCIER_PATRONEN, ...extraNamen.map(normTekst)]
  return patronen.some((p) => {
    const pn = normTekst(p)
    return pn.length > 0 && (n.includes(pn) || compact.includes(pn.replace(/[^a-z0-9]/g, '')))
  })
}

const KOSTENSOORT_REGELS: ReadonlyArray<{ soort: BrandstofKostensoort; re: RegExp }> = [
  { soort: 'ADBLUE', re: /\bad[\s-]?blue\b/ },
  { soort: 'WASSEN', re: /\b(wassen|wasstraat|carwash|car wash|autowas\w*|wasbox|washbox|wasprogramma)\b/ },
  { soort: 'TOL', re: /\b(tol|tolgeld|tolweg|vignet|maut|peage|tunnel\w*)\b/ },
  {
    soort: 'KAARTKOSTEN',
    re: /\b(kaartkosten|pasabonnement|pasbeheer|pas(sen)?kosten|card fee|kaart fee|beheerkosten|abonnementskosten)\b/,
  },
  {
    soort: 'TOESLAG',
    re: /\b(toeslag\w*|servicekosten|transactiekosten|administratiekosten|surcharge|handling\w*|bemand\w*)\b/,
  },
  {
    soort: 'BRANDSTOF',
    re: /\b(euro ?9[58]|e10|e5|benzine|diesel\w*|hvo\w*|lpg|cng|lng|gtl|super ?plus|premium|laden|laadsessie|laadpaal|elektri\w*|kwh|brandstof)\b/,
  },
]

/**
 * Kostensoort uit de producttekst van een MKB-regel. `zeker = false` = geen regel
 * herkend: de regel wordt OVERIG en de extractie markeert hem (nooit stil).
 */
export function classificeerBrandstofKostensoort(
  tekst: string | null | undefined,
): { kostensoort: BrandstofKostensoort; zeker: boolean } {
  if (!tekst) return { kostensoort: 'OVERIG', zeker: false }
  const n = normTekst(tekst)
  for (const r of KOSTENSOORT_REGELS) {
    if (r.re.test(n)) return { kostensoort: r.soort, zeker: true }
  }
  return { kostensoort: 'OVERIG', zeker: false }
}

export function isBrandstofKostensoort(v: unknown): v is BrandstofKostensoort {
  return typeof v === 'string' && (BRANDSTOF_KOSTENSOORT as readonly string[]).includes(v)
}

// ─── Totaalcontrole ──────────────────────────────────────────────────────────

export interface TotaalControleInput {
  regels: ReadonlyArray<{ bedragExclBtw: number; btwBedrag: number }>
  factuurTotaalInclBtw: number | null
  /** Optioneel: excl.-totaal van de factuur (sluit dan óók op excl.). */
  factuurTotaalExclBtw?: number | null
}

export interface TotaalControleUitkomst {
  regelsomExclBtw: number
  regelsomBtw: number
  regelsomInclBtw: number
  /** Regelsom incl. btw min factuurtotaal incl. btw; null = factuurtotaal onbekend. */
  verschilInclBtw: number | null
  /** Idem excl. btw; null = onbekend. */
  verschilExclBtw: number | null
  sluit: boolean
}

/**
 * Som van de regels + btw moet exact (op de cent) het factuurtotaal zijn. Een
 * onbekend factuurtotaal sluit per definitie niet: dan blijft de factuur te
 * controleren (nooit half boeken). Creditregels tellen negatief mee.
 */
export function controleerFactuurTotaal(input: TotaalControleInput): TotaalControleUitkomst {
  let excl = 0
  let btw = 0
  for (const r of input.regels) {
    excl += naarCenten(r.bedragExclBtw)
    btw += naarCenten(r.btwBedrag)
  }
  const incl = excl + btw
  const verschilIncl =
    input.factuurTotaalInclBtw == null ? null : incl - naarCenten(input.factuurTotaalInclBtw)
  const verschilExcl =
    input.factuurTotaalExclBtw == null ? null : excl - naarCenten(input.factuurTotaalExclBtw)
  const sluit = verschilIncl === 0 && (verschilExcl === null || verschilExcl === 0)
  return {
    regelsomExclBtw: naarEuro(excl),
    regelsomBtw: naarEuro(btw),
    regelsomInclBtw: naarEuro(incl),
    verschilInclBtw: verschilIncl === null ? null : naarEuro(verschilIncl),
    verschilExclBtw: verschilExcl === null ? null : naarEuro(verschilExcl),
    sluit,
  }
}

// ─── Dedupe tegen bestaande tankpas-transacties ──────────────────────────────

export interface DedupeKandidaat {
  id: string
  fuelCardId: string
  datum: Date
  bedragInclBtw: number
  liters: number | null
  /** Al gekoppeld aan een (andere) actieve factuurregel. */
  gekoppeld: boolean
}

export interface DedupeRegel {
  fuelCardIds: readonly string[]
  transactieOp: Date
  bedragInclBtw: number
  hoeveelheid: number | null
}

export interface DedupeUitkomst {
  kandidaat: DedupeKandidaat | null
  /** Er waren wel passende transacties, maar allemaal al aan een andere regel gekoppeld. */
  alleenGekoppeld: boolean
}

/**
 * Zoek de bestaande tankbeurt voor een factuurregel: zelfde tankpas, NL-dag binnen
 * de dag-tolerantie, bedrag incl. btw binnen de bedrag-tolerantie. Voorkeur:
 * niet-gekoppeld, kleinste bedragverschil, dan kleinste dagverschil, dan liters.
 */
export function kiesBestaandeTransactie(
  regel: DedupeRegel,
  kandidaten: readonly DedupeKandidaat[],
  opts: { dagTolerantie: number; bedragTolerantie: number },
): DedupeUitkomst {
  const regelDag = nlDag(regel.transactieOp)
  const tolCenten = naarCenten(opts.bedragTolerantie)
  const passend = kandidaten
    .filter((k) => regel.fuelCardIds.includes(k.fuelCardId))
    .map((k) => ({
      k,
      dagen: Math.abs(dagVerschil(regelDag, nlDag(k.datum))),
      bedrag: Math.abs(naarCenten(k.bedragInclBtw) - naarCenten(regel.bedragInclBtw)),
      liters:
        k.liters != null && regel.hoeveelheid != null
          ? Math.abs(k.liters - regel.hoeveelheid)
          : 0,
    }))
    .filter((x) => x.dagen <= opts.dagTolerantie && x.bedrag <= tolCenten)
  if (passend.length === 0) return { kandidaat: null, alleenGekoppeld: false }
  const vrij = passend.filter((x) => !x.k.gekoppeld)
  if (vrij.length === 0) return { kandidaat: null, alleenGekoppeld: true }
  vrij.sort((a, b) => a.bedrag - b.bedrag || a.dagen - b.dagen || a.liters - b.liters || a.k.id.localeCompare(b.k.id))
  return { kandidaat: vrij[0]!.k, alleenGekoppeld: false }
}

// ─── Loonmapping ─────────────────────────────────────────────────────────────

/** Genormaliseerde omschrijving: kleine letters, zonder accenten, enkele spaties. */
export function normaliseerLoonOmschrijving(s: string): string {
  return normTekst(s).replace(/^[\s.:;,\-]+|[\s.:;,\-]+$/g, '')
}

function geldigIn(m: LoonMappingKandidaat, periode: number): boolean {
  return m.geldigVanaf <= periode && (m.geldigTot == null || m.geldigTot >= periode)
}

export interface LoonCategorieResolutie {
  categorie: KostprijsLoonCategorie | null
  mappingId: string | null
  /** Meerdere even specifieke BEVAT-patronen met verschillende categorieen. */
  ambigu: boolean
  kandidaten: KostprijsLoonCategorie[]
}

/**
 * Welke categorie geldt voor deze omschrijving in deze periode? EXACT wint; anders
 * het langste BEVAT-patroon. Twee even lange BEVAT-patronen met een verschillende
 * categorie = ambigu (geen gok -> te-mappen). Alleen versies die in de periode
 * geldig zijn tellen (versie-vast).
 */
export function resolveLoonCategorie(
  omschrijving: string,
  periode: number,
  mappings: readonly LoonMappingKandidaat[],
): LoonCategorieResolutie {
  const n = normaliseerLoonOmschrijving(omschrijving)
  if (n.length === 0) return { categorie: null, mappingId: null, ambigu: false, kandidaten: [] }
  const geldig = mappings.filter((m) => geldigIn(m, periode))
  const exact = geldig.filter((m) => m.matchWijze === 'EXACT' && m.patroon === n)
  if (exact.length > 0) {
    const nieuwste = [...exact].sort((a, b) => b.geldigVanaf - a.geldigVanaf)[0]!
    return { categorie: nieuwste.categorie, mappingId: nieuwste.id, ambigu: false, kandidaten: [nieuwste.categorie] }
  }
  const bevat = geldig.filter((m) => m.matchWijze === 'BEVAT' && m.patroon.length > 0 && n.includes(m.patroon))
  if (bevat.length === 0) return { categorie: null, mappingId: null, ambigu: false, kandidaten: [] }
  const maxLen = Math.max(...bevat.map((m) => m.patroon.length))
  const langste = bevat.filter((m) => m.patroon.length === maxLen)
  const categorieen = [...new Set(langste.map((m) => m.categorie))]
  if (categorieen.length > 1) {
    return { categorie: null, mappingId: null, ambigu: true, kandidaten: categorieen }
  }
  const gekozen = [...langste].sort((a, b) => b.geldigVanaf - a.geldigVanaf)[0]!
  return { categorie: gekozen.categorie, mappingId: gekozen.id, ambigu: false, kandidaten: categorieen }
}

/** Extractie-categorie (LoonstrookComponentCategorie) -> kostprijscategorie als hint. */
export function extractieCategorieNaarKostprijs(
  aiCategorie: string | null | undefined,
  omschrijving: string,
): KostprijsLoonCategorie | null {
  const n = normaliseerLoonOmschrijving(omschrijving)
  switch (aiCategorie) {
    case 'BASIS':
      return 'BASISLOON'
    case 'OVERWERK':
      return 'TOESLAG_OVERWERK'
    case 'ONREGELMATIGHEID':
      return /zaterdag|zondag|feestdag|weekend/.test(n) ? 'TOESLAG_WEEKEND_FEESTDAG' : 'TOESLAG_PLOEG'
    case 'REISUREN':
      return 'REISUREN'
    case 'REISVERGOEDING':
      return 'VERGOEDING_ONBELAST'
    case 'BIJTELLING':
      return 'BIJTELLING'
    case 'PROVISIE':
    case 'EINDEJAAR':
      return 'OVERIG'
    case 'VAKANTIEGELD':
      // Uitbetaling van het maandelijks gereserveerde vakantiegeld: telt niet nog eens.
      return 'INFORMATIEF'
    default:
      return null
  }
}

function tokens(s: string): Set<string> {
  return new Set(normaliseerLoonOmschrijving(s).split(/[^a-z0-9%]+/).filter((t) => t.length > 1))
}

function jaccard(a: Set<string>, b: Set<string>): number {
  if (a.size === 0 || b.size === 0) return 0
  let doorsnede = 0
  for (const t of a) if (b.has(t)) doorsnede++
  return doorsnede / (a.size + b.size - doorsnede)
}

/**
 * Suggestie voor een te-mappen omschrijving: eerst de meest gelijkende eerdere
 * mapping (historie, Jaccard >= 0,5), dan een trefwoord uit de standaardlijst, dan
 * de categorie die de strook-extractie gaf. Alleen een SUGGESTIE: de mens beslist.
 */
export function suggereerLoonCategorie(input: {
  omschrijving: string
  aiCategorie: string | null
  historie: ReadonlyArray<{ patroon: string; categorie: KostprijsLoonCategorie }>
}): { categorie: KostprijsLoonCategorie | null; bron: LoonSuggestieBron | null } {
  const eigen = tokens(input.omschrijving)
  let beste: { categorie: KostprijsLoonCategorie; score: number } | null = null
  for (const h of input.historie) {
    const score = jaccard(eigen, tokens(h.patroon))
    if (score >= 0.5 && (!beste || score > beste.score)) beste = { categorie: h.categorie, score }
  }
  if (beste) return { categorie: beste.categorie, bron: 'HISTORIE' }
  const n = normaliseerLoonOmschrijving(input.omschrijving)
  const trefwoord = [...LOON_COMPONENT_MAPPING_SEED]
    .filter((s) => n.includes(s.patroon))
    .sort((a, b) => b.patroon.length - a.patroon.length)[0]
  if (trefwoord) return { categorie: trefwoord.categorie, bron: 'TREFWOORD' }
  const ai = extractieCategorieNaarKostprijs(input.aiCategorie, input.omschrijving)
  if (ai) return { categorie: ai, bron: 'EXTRACTIE' }
  return { categorie: null, bron: null }
}

// ─── Loonkost-aggregatie per medewerker-periode ──────────────────────────────

export interface LoonStrookComponentInput {
  categorie: string
  omschrijving: string
  aantal: number | null
  bedragPeriode: number
}

export interface LoonStrookInhoudingInput {
  categorie: string
  omschrijving: string
  /** Positief ingehouden bedrag (zoals de extractie hem bewaart). */
  bedrag: number
}

export interface LoonStrookInput {
  id: string
  runNummer: number
  componenten: readonly LoonStrookComponentInput[]
  inhoudingen: readonly LoonStrookInhoudingInput[]
  bijtellingBruto: number | null
}

export interface LoonkostBucketWaarde {
  bedrag: number
  aantal: number | null
  aantalComponenten: number
}

export interface LoonTeMappenRegel {
  patroon: string
  omschrijving: string
  bedrag: number
  aiCategorie: string | null
}

export interface LoonkostAggregaat {
  perBucket: Record<LoonkostBucket, LoonkostBucketWaarde>
  loonkostTellend: number
  nogTeClassificeren: number
  teMappen: LoonTeMappenRegel[]
  /** Bijtelling (positief) en eigen bijdrage (negatief). */
  bijtelling: number
  eigenBijdrage: number
  bijtellingBron: 'STROOK_COMPONENTEN' | 'STROOK_TOTAAL' | null
  reisurenAantal: number | null
  aantalStroken: number
  aantalRuns: number
}

function legeBuckets(): Record<LoonkostBucket, { centen: number; aantal: number | null; n: number }> {
  const out = {} as Record<LoonkostBucket, { centen: number; aantal: number | null; n: number }>
  for (const b of LOONKOST_BUCKETS) out[b] = { centen: 0, aantal: null, n: 0 }
  return out
}

/**
 * Tel de componenten van alle stroken (alle runs, FO-422) van één medewerker-periode
 * op per kostprijscategorie. Een component zonder geldige mapping gaat naar
 * NOG_TE_CLASSIFICEREN (zichtbaar + te-mappen-regel), nooit stil naar OVERIG.
 * Inhoudingen zonder mapping blijven INHOUDING (de extractie heeft ze als
 * inhouding herkend) en tellen negatief; eigen bijdrage is altijd negatief.
 * Correcties (negatieve componenten) behouden hun teken.
 */
export function aggregeerLoonkost(
  stroken: readonly LoonStrookInput[],
  periode: number,
  mappings: readonly LoonMappingKandidaat[],
): LoonkostAggregaat {
  const b = legeBuckets()
  const teMappen = new Map<string, { omschrijving: string; centen: number; aiCategorie: string | null }>()
  let bijtellingComponentCenten = 0
  let heeftBijtellingComponent = false
  let bijtellingTotaalCenten = 0
  let heeftBijtellingTotaal = false

  const voegToe = (bucket: LoonkostBucket, centen: number, aantal: number | null) => {
    const cur = b[bucket]
    cur.centen += centen
    cur.n += 1
    if (aantal != null && Number.isFinite(aantal)) cur.aantal = rond2((cur.aantal ?? 0) + aantal)
  }

  for (const s of stroken) {
    for (const c of s.componenten) {
      const res = resolveLoonCategorie(c.omschrijving, periode, mappings)
      const centen = naarCenten(c.bedragPeriode)
      if (res.categorie == null) {
        voegToe(NOG_TE_CLASSIFICEREN, centen, c.aantal)
        const patroon = normaliseerLoonOmschrijving(c.omschrijving) || '(zonder omschrijving)'
        const cur = teMappen.get(patroon) ?? { omschrijving: c.omschrijving, centen: 0, aiCategorie: c.categorie }
        cur.centen += centen
        teMappen.set(patroon, cur)
        continue
      }
      if (res.categorie === 'EIGEN_BIJDRAGE') {
        voegToe('EIGEN_BIJDRAGE', -Math.abs(centen), c.aantal)
        continue
      }
      if (res.categorie === 'BIJTELLING') {
        heeftBijtellingComponent = true
        bijtellingComponentCenten += centen
      }
      voegToe(res.categorie, centen, c.aantal)
    }
    for (const i of s.inhoudingen) {
      const res = resolveLoonCategorie(i.omschrijving, periode, mappings)
      const centen = -Math.abs(naarCenten(i.bedrag))
      const cat: KostprijsLoonCategorie =
        res.categorie === 'EIGEN_BIJDRAGE' ? 'EIGEN_BIJDRAGE' : 'INHOUDING'
      voegToe(cat, centen, null)
    }
    if (s.bijtellingBruto != null && s.bijtellingBruto !== 0) {
      heeftBijtellingTotaal = true
      bijtellingTotaalCenten += naarCenten(s.bijtellingBruto)
    }
  }

  // Bijtelling: de componenten zijn leidend; alleen zonder bijtellingregel valt de
  // registratie terug op het strook-totaal (FO-433-veld) - nooit beide optellen.
  let bijtellingCenten = 0
  let bijtellingBron: LoonkostAggregaat['bijtellingBron'] = null
  if (heeftBijtellingComponent) {
    bijtellingCenten = bijtellingComponentCenten
    bijtellingBron = 'STROOK_COMPONENTEN'
  } else if (heeftBijtellingTotaal) {
    bijtellingCenten = bijtellingTotaalCenten
    bijtellingBron = 'STROOK_TOTAAL'
    b.BIJTELLING.centen += bijtellingTotaalCenten
  }

  const perBucket = {} as Record<LoonkostBucket, LoonkostBucketWaarde>
  for (const bucket of LOONKOST_BUCKETS) {
    const cur = b[bucket]
    perBucket[bucket] = { bedrag: naarEuro(cur.centen), aantal: cur.aantal, aantalComponenten: cur.n }
  }
  let tellend = 0
  for (const cat of KOSTPRIJS_TELLENDE_LOON_CATEGORIEEN) tellend += b[cat].centen

  return {
    perBucket,
    loonkostTellend: naarEuro(tellend),
    nogTeClassificeren: naarEuro(b.NOG_TE_CLASSIFICEREN.centen),
    teMappen: [...teMappen.entries()]
      .map(([patroon, v]) => ({
        patroon,
        omschrijving: v.omschrijving,
        bedrag: naarEuro(v.centen),
        aiCategorie: v.aiCategorie,
      }))
      .sort((x, y) => x.patroon.localeCompare(y.patroon)),
    bijtelling: naarEuro(bijtellingCenten),
    eigenBijdrage: naarEuro(b.EIGEN_BIJDRAGE.centen),
    bijtellingBron,
    reisurenAantal: b.REISUREN.aantal,
    aantalStroken: stroken.length,
    aantalRuns: new Set(stroken.map((s) => s.runNummer)).size,
  }
}

// ─── Bijtelling-toerekening pro rata (berijder-historie) ─────────────────────

export interface ToewijzingVoorToerekening {
  voertuigId: string
  /** VAST | TIJDELIJK */
  soort: string
  startDatum: Date
  eindDatum: Date | null
}

export interface BijtellingToerekeningDeel {
  voertuigId: string
  /** Eerste NL-dag (UTC-instant van NL-middernacht). */
  vanaf: Date
  /** Exclusief: NL-middernacht na de laatste dag. */
  tot: Date
  dagen: number
  aandeel: number
  bijtellingDeel: number
  eigenBijdrageDeel: number
}

export interface BijtellingToerekeningUitkomst {
  status: Exclude<BijtellingToerekeningStatus, 'HANDMATIG'>
  twijfelReden: string | null
  delen: BijtellingToerekeningDeel[]
}

function ymdPlus(ymd: string, dagen: number): string {
  const d = new Date(Date.UTC(Number(ymd.slice(0, 4)), Number(ymd.slice(5, 7)) - 1, Number(ymd.slice(8, 10))))
  d.setUTCDate(d.getUTCDate() + dagen)
  return d.toISOString().slice(0, 10)
}

/** Verdeel een bedrag in centen over gewichten; de rest-cent gaat naar het grootste deel. */
export function verdeelCenten(totaalCenten: number, gewichten: readonly number[]): number[] {
  const som = gewichten.reduce((s, g) => s + g, 0)
  if (som <= 0 || gewichten.length === 0) return gewichten.map(() => 0)
  const ruw = gewichten.map((g) => Math.trunc((totaalCenten * g) / som))
  let rest = totaalCenten - ruw.reduce((s, r) => s + r, 0)
  const volgorde = gewichten
    .map((g, i) => ({ g, i }))
    .sort((a, b) => b.g - a.g || a.i - b.i)
  let k = 0
  while (rest !== 0 && volgorde.length > 0) {
    const idx = volgorde[k % volgorde.length]!.i
    ruw[idx] = ruw[idx]! + (rest > 0 ? 1 : -1)
    rest += rest > 0 ? -1 : 1
    k++
  }
  return ruw
}

// ─── Voertuig per NL-dag (berijder-historie, tijd-nauwkeurig) ────────────────

/** Onder deze overlap (klokverschil bij een wissel) tellen twee toewijzingen niet als tegelijk. */
const MIN_OVERLAP_MS = 60_000

export interface VoertuigDagAandeel {
  /** jjjj-mm-dd (NL). */
  dag: string
  /** Deel van de dag per voertuig (samen 1 als er die dag een voertuig was). */
  aandelen: Array<{ voertuigId: string; aandeel: number }>
  /** Twee verschillende voertuigen van dezelfde soort echt tegelijk actief (overlap in tijd). */
  overlap: boolean
}

/**
 * Welk voertuig reed de medewerker per NL-dag van de periode, en welk deel van de dag?
 * Een toewijzing is het tijdvak [start, eind) (eind leeg = open). Per dag telt het VASTE
 * voertuig; alleen zonder vast voertuig telt een TIJDELIJKE (vervangende) auto. Een wissel
 * op een dag (eind van de oude = start van de nieuwe, zoals de wissel-route vastlegt) deelt
 * de dag naar rato van de tijd; alleen twee voertuigen die echt tegelijk actief zijn geven
 * `overlap` (twijfel). Eén gedeelde bron voor de bijtelling-toerekening en de vaste
 * voertuigkosten.
 */
export function verdeelVoertuigDagen(
  toewijzingen: readonly ToewijzingVoorToerekening[],
  periode: number,
): VoertuigDagAandeel[] {
  const { jaar, maand } = periodeUitSleutel(periode)
  const eersteDag = `${jaar}-${String(maand).padStart(2, '0')}-01`
  const uit: VoertuigDagAandeel[] = []
  for (let i = 0; i < dagenInPeriode(periode); i++) {
    const dag = ymdPlus(eersteDag, i)
    const ds = amsterdamAllDayStartToUtc(dag).getTime()
    const de = amsterdamAllDayStartToUtc(ymdPlus(dag, 1)).getTime()
    const segmenten = (lijst: readonly ToewijzingVoorToerekening[]) =>
      lijst
        .map((t) => ({
          voertuigId: t.voertuigId,
          s: Math.max(t.startDatum.getTime(), ds),
          e: Math.min(t.eindDatum ? t.eindDatum.getTime() : de, de),
        }))
        .filter((x) => x.e > x.s)
    const vast = segmenten(toewijzingen.filter((t) => t.soort === 'VAST'))
    const kand = vast.length > 0 ? vast : segmenten(toewijzingen.filter((t) => t.soort !== 'VAST'))

    let overlap = false
    for (let a = 0; a < kand.length && !overlap; a++) {
      for (let b = a + 1; b < kand.length; b++) {
        const x = kand[a]!
        const y = kand[b]!
        if (x.voertuigId === y.voertuigId) continue
        if (Math.min(x.e, y.e) - Math.max(x.s, y.s) > MIN_OVERLAP_MS) {
          overlap = true
          break
        }
      }
    }

    // Per voertuig de unie van zijn tijdvakken op deze dag.
    const perVoertuig = new Map<string, Array<{ s: number; e: number }>>()
    for (const k of kand) perVoertuig.set(k.voertuigId, [...(perVoertuig.get(k.voertuigId) ?? []), { s: k.s, e: k.e }])
    const duur = new Map<string, number>()
    for (const [voertuigId, lijst] of perVoertuig) {
      const gesorteerd = [...lijst].sort((p, q) => p.s - q.s)
      let totaal = 0
      let curS = gesorteerd[0]!.s
      let curE = gesorteerd[0]!.e
      for (const seg of gesorteerd.slice(1)) {
        if (seg.s <= curE) {
          curE = Math.max(curE, seg.e)
        } else {
          totaal += curE - curS
          curS = seg.s
          curE = seg.e
        }
      }
      duur.set(voertuigId, totaal + (curE - curS))
    }
    const som = [...duur.values()].reduce((s, d) => s + d, 0)
    uit.push({
      dag,
      aandelen: som > 0 ? [...duur.entries()].map(([voertuigId, d]) => ({ voertuigId, aandeel: d / som })) : [],
      overlap,
    })
  }
  return uit
}

/**
 * Reken de bijtelling + eigen bijdrage van een periode toe aan de voertuigen die de
 * medewerker in die periode reed, pro rata op de (delen van) dagen uit
 * `verdeelVoertuigDagen`: een wissel midden op een dag deelt die dag naar rato van de
 * tijd; een vervangende TIJDELIJKE auto verandert de bijtelling niet. Twee voertuigen echt
 * tegelijk op naam = twijfel -> handmatig. Dagen zonder voertuig tellen niet (de strook
 * rekent dan zelf al pro rata). Geen enkele dag met voertuig = GEEN_VOERTUIG.
 */
export function berekenBijtellingToerekening(input: {
  periode: number
  bijtelling: number
  eigenBijdrage: number
  toewijzingen: readonly ToewijzingVoorToerekening[]
}): BijtellingToerekeningUitkomst {
  const { jaar, maand } = periodeUitSleutel(input.periode)
  const eersteDag = `${jaar}-${String(maand).padStart(2, '0')}-01`
  const dagen = verdeelVoertuigDagen(input.toewijzingen, input.periode)
  const twijfel = dagen.find((d) => d.overlap)
  if (twijfel) {
    const [j, m, d] = twijfel.dag.split('-')
    return {
      status: 'TWIJFEL',
      twijfelReden: `Op ${d}-${m}-${j} stonden meerdere voertuigen tegelijk op naam; kies de verdeling handmatig.`,
      delen: [],
    }
  }

  const volgorde: string[] = []
  const perVoertuig = new Map<string, { eerste: number; laatste: number; dagen: number }>()
  dagen.forEach((d, i) => {
    for (const a of d.aandelen) {
      const cur = perVoertuig.get(a.voertuigId)
      if (cur) {
        cur.laatste = i
        cur.dagen += a.aandeel
      } else {
        perVoertuig.set(a.voertuigId, { eerste: i, laatste: i, dagen: a.aandeel })
        volgorde.push(a.voertuigId)
      }
    }
  })
  if (volgorde.length === 0) {
    return { status: 'GEEN_VOERTUIG', twijfelReden: null, delen: [] }
  }

  const gewichten = volgorde.map((v) => perVoertuig.get(v)!.dagen)
  const totaalDagen = gewichten.reduce((s, g) => s + g, 0)
  const bijtellingDelen = verdeelCenten(naarCenten(input.bijtelling), gewichten)
  const eigenBijdrageDelen = verdeelCenten(naarCenten(input.eigenBijdrage), gewichten)
  return {
    status: 'AUTO',
    twijfelReden: null,
    delen: volgorde.map((v, i) => {
      const info = perVoertuig.get(v)!
      return {
        voertuigId: v,
        vanaf: amsterdamAllDayStartToUtc(ymdPlus(eersteDag, info.eerste)),
        tot: amsterdamAllDayStartToUtc(ymdPlus(eersteDag, info.laatste + 1)),
        dagen: rond2(info.dagen),
        aandeel: Math.round((info.dagen / totaalDagen) * 1_000_000) / 1_000_000,
        bijtellingDeel: naarEuro(bijtellingDelen[i] ?? 0),
        eigenBijdrageDeel: naarEuro(eigenBijdrageDelen[i] ?? 0),
      }
    }),
  }
}

// ─── Brandstof-aggregatie + verbruik ─────────────────────────────────────────

export interface BrandstofKoppelingInput {
  kostensoort: BrandstofKostensoort
  bedragExclBtw: number
  hoeveelheid: number | null
  /** Eenheid van de hoeveelheid (L, kWh, st); leeg = liters. */
  eenheid?: string | null
  isPrive: boolean
  poolgebruik: boolean
  transactieOp: Date
  kmStand: number | null
}

/** Is de hoeveelheid in liters (leeg = liters)? kWh (laden) en stuks tellen niet als liters. */
export function isLiterEenheid(eenheid: string | null | undefined): boolean {
  if (eenheid == null) return true
  const e = eenheid.trim().toLowerCase()
  return e === '' || e === 'l' || e === 'ltr' || e === 'liter' || e === 'liters'
}

export interface BrandstofAggregaat {
  /** Excl. btw, excl. privé. */
  totaal: number
  prive: number
  /** Alleen kostensoort BRANDSTOF, excl. privé. */
  liters: number
  perKostensoort: Record<BrandstofKostensoort, { bedrag: number; hoeveelheid: number; aantal: number }>
  aantal: number
  aantalPoolgebruik: number
}

export function aggregeerBrandstof(koppelingen: readonly BrandstofKoppelingInput[]): BrandstofAggregaat {
  const per = {} as Record<BrandstofKostensoort, { centen: number; hoeveelheid: number; aantal: number }>
  for (const s of BRANDSTOF_KOSTENSOORT) per[s] = { centen: 0, hoeveelheid: 0, aantal: 0 }
  let totaal = 0
  let prive = 0
  let liters = 0
  let pool = 0
  for (const k of koppelingen) {
    const centen = naarCenten(k.bedragExclBtw)
    if (k.poolgebruik) pool++
    if (k.isPrive) {
      prive += centen
      continue
    }
    totaal += centen
    const cur = per[k.kostensoort]
    cur.centen += centen
    cur.aantal += 1
    if (k.hoeveelheid != null) cur.hoeveelheid = Math.round((cur.hoeveelheid + k.hoeveelheid) * 1000) / 1000
    if (k.kostensoort === 'BRANDSTOF' && k.hoeveelheid != null && isLiterEenheid(k.eenheid)) {
      liters = Math.round((liters + k.hoeveelheid) * 1000) / 1000
    }
  }
  const perKostensoort = {} as BrandstofAggregaat['perKostensoort']
  for (const s of BRANDSTOF_KOSTENSOORT) {
    perKostensoort[s] = { bedrag: naarEuro(per[s].centen), hoeveelheid: per[s].hoeveelheid, aantal: per[s].aantal }
  }
  return { totaal: naarEuro(totaal), prive: naarEuro(prive), liters, perKostensoort, aantal: koppelingen.length, aantalPoolgebruik: pool }
}

/**
 * Verbruik volgens de tank-tot-tank-methode: km = laatste minus eerste km-stand in
 * de periode; liters = alles wat ná de eerste stand getankt is (de eerste tankbeurt
 * vult wat vóór de periode verreden is). Minder dan twee standen of geen afstand ->
 * niet te bepalen (null). Een km-registratie mag de standen aanvullen.
 */
export function berekenVerbruik(input: {
  tankbeurten: ReadonlyArray<{ op: Date; liters: number; kmStand: number | null }>
  kmRegistraties?: ReadonlyArray<{ op: Date; kmStand: number }>
}): { kmVerreden: number | null; verbruikPer100Km: number | null } {
  const metStand = input.tankbeurten
    .filter((t) => t.kmStand != null && t.kmStand > 0)
    .sort((a, b) => a.op.getTime() - b.op.getTime())
  if (metStand.length >= 2) {
    const eerste = metStand[0]!
    const laatste = metStand[metStand.length - 1]!
    const km = (laatste.kmStand ?? 0) - (eerste.kmStand ?? 0)
    if (km > 0) {
      const liters = metStand.slice(1).reduce((s, t) => s + t.liters, 0)
      return {
        kmVerreden: Math.round(km * 10) / 10,
        verbruikPer100Km: liters > 0 ? Math.round((liters / km) * 100 * 100) / 100 : null,
      }
    }
  }
  const reg = [...(input.kmRegistraties ?? [])].sort((a, b) => a.op.getTime() - b.op.getTime())
  if (reg.length >= 2) {
    const km = reg[reg.length - 1]!.kmStand - reg[0]!.kmStand
    if (km > 0) return { kmVerreden: km, verbruikPer100Km: null }
  }
  return { kmVerreden: null, verbruikPer100Km: null }
}

/**
 * Wijkt het verbruik van deze maand sterk af van het eigen voertuiggemiddelde?
 * Afwijkend bij >= factor x of <= 1/factor x het gemiddelde van de historie
 * (mogelijk een verkeerde pas of een verkeerd kenteken). Te weinig historie -> nee.
 */
export function beoordeelVerbruikAfwijking(input: {
  huidig: number | null
  historie: readonly number[]
  factor: number
  minHistorie: number
}): { afwijkend: boolean; richting: 'HOOG' | 'LAAG' | null; gemiddelde: number | null; verhouding: number | null } {
  const hist = input.historie.filter((h) => Number.isFinite(h) && h > 0)
  if (input.huidig == null || input.huidig <= 0 || hist.length < input.minHistorie) {
    return { afwijkend: false, richting: null, gemiddelde: null, verhouding: null }
  }
  const gemiddelde = hist.reduce((s, h) => s + h, 0) / hist.length
  const verhouding = input.huidig / gemiddelde
  const hoog = verhouding >= input.factor
  const laag = verhouding <= 1 / input.factor
  return {
    afwijkend: hoog || laag,
    richting: hoog ? 'HOOG' : laag ? 'LAAG' : null,
    gemiddelde: rond2(gemiddelde),
    verhouding: Math.round(verhouding * 100) / 100,
  }
}

// ─── Werkelijke kostprijs naast de norm ──────────────────────────────────────

export interface WerkelijkeKostprijsInput {
  /** Null = geen loonstrook in de periode (loonkost dan uit de norm, gemarkeerd). */
  loon: { perBucket: Record<LoonkostBucket, number>; nogTeClassificeren: number } | null
  params: {
    werkgeverlastenPct: number
    vakantiegeldPct: number
    pensioenPct: number
    overheadPct: number
  }
  norm: {
    loonkost: number
    werkgeverslasten: number
    opleiding: number
    opslagen: number
    bijkomend: number
  }
  brandstof: number
  voertuigVast: number
  onderhoud: number
  schade: number
  onderhoudUitNorm: boolean
  productieveUren: number | null
}

export interface WerkelijkeKostprijsUitkomst {
  loonkost: number
  werkgeverslasten: number
  werkgeverslastenBron: WerkgeverslastenBron
  opslagen: number
  bijkomend: number
  brandstof: number
  voertuigVast: number
  onderhoud: number
  schade: number
  totaal: number
  kostprijsPerProductiefUur: number | null
  markeringen: KostprijsMarkering[]
}

/**
 * De werkelijke maandkosten van een medewerker:
 *   loonkost        = bruto loon (basis + toeslagen + reisuren + overig) + onbelaste
 *                     vergoedingen, uit de strook (anders de norm, gemarkeerd);
 *   werkgeverslasten = afgeleid (werkgeverslasten% + vakantiegeld% + pensioen% over het
 *                     werkelijke bruto loon), tenzij de strook zelf meer toont: een strook
 *                     laat vaak maar een deel zien (alleen het werkgeversdeel pensioen), dus
 *                     het hoogste van beide telt - nooit instorten, nooit dubbel. Vakantiegeld
 *                     is zo maandelijks gereserveerd; de uitbetaling is INFORMATIEF;
 *   opslagen        = overhead% over loon + werkgeverslasten, plus de opleiding uit de
 *                     norm (staat niet op de strook);
 *   + bijkomende kosten (norm) + brandstof + voertuig vast + onderhoud + schade.
 * Bijtelling, eigen bijdrage en inhoudingen tellen NIET; nog-te-classificeren telt
 * niet mee maar markeert de kostprijs als onvolledig. Ziekte zit in de werkelijkheid
 * (loondoorbetaling + minder productieve uren), dus geen ziekte-opslag. De kostprijs
 * per productief uur deelt door de productieve uren (reisuren tellen daar NIET in).
 */
export function berekenWerkelijkeKostprijs(input: WerkelijkeKostprijsInput): WerkelijkeKostprijsUitkomst {
  const markeringen: KostprijsMarkering[] = []
  let loonkostCenten: number
  let werkgeverslastenCenten: number
  let bron: WerkgeverslastenBron
  if (input.loon) {
    let brutoCenten = 0
    for (const cat of KOSTPRIJS_BRUTO_LOON_CATEGORIEEN) brutoCenten += naarCenten(input.loon.perBucket[cat] ?? 0)
    loonkostCenten = brutoCenten + naarCenten(input.loon.perBucket.VERGOEDING_ONBELAST ?? 0)
    const opStrook = naarCenten(input.loon.perBucket.WERKGEVERSLASTEN ?? 0)
    const pct = input.params.werkgeverlastenPct + input.params.vakantiegeldPct + input.params.pensioenPct
    const afgeleid = Math.round((brutoCenten * pct) / 100)
    if (opStrook !== 0 && opStrook >= afgeleid) {
      werkgeverslastenCenten = opStrook
      bron = 'STROOK'
    } else {
      werkgeverslastenCenten = afgeleid
      bron = 'AFGELEID'
      markeringen.push('WERKGEVERSLASTEN_AFGELEID')
    }
    if (input.loon.nogTeClassificeren !== 0) markeringen.push('NOG_TE_CLASSIFICEREN')
  } else {
    loonkostCenten = naarCenten(input.norm.loonkost)
    werkgeverslastenCenten = naarCenten(input.norm.werkgeverslasten)
    bron = 'AFGELEID'
    markeringen.push('GEEN_LOONSTROOK')
  }
  const opslagenCenten = input.loon
    ? Math.round(((loonkostCenten + werkgeverslastenCenten) * input.params.overheadPct) / 100) +
      naarCenten(input.norm.opleiding)
    : naarCenten(input.norm.opslagen)
  const bijkomendCenten = naarCenten(input.norm.bijkomend)
  markeringen.push('BIJKOMEND_NORM')
  if (input.onderhoudUitNorm) markeringen.push('ONDERHOUD_NORM')
  const totaalCenten =
    loonkostCenten +
    werkgeverslastenCenten +
    opslagenCenten +
    bijkomendCenten +
    naarCenten(input.brandstof) +
    naarCenten(input.voertuigVast) +
    naarCenten(input.onderhoud) +
    naarCenten(input.schade)
  const uren = input.productieveUren
  if (uren == null || uren <= 0) markeringen.push('GEEN_UREN')
  return {
    loonkost: naarEuro(loonkostCenten),
    werkgeverslasten: naarEuro(werkgeverslastenCenten),
    werkgeverslastenBron: bron,
    opslagen: naarEuro(opslagenCenten),
    bijkomend: naarEuro(bijkomendCenten),
    brandstof: rond2(input.brandstof),
    voertuigVast: rond2(input.voertuigVast),
    onderhoud: rond2(input.onderhoud),
    schade: rond2(input.schade),
    totaal: naarEuro(totaalCenten),
    kostprijsPerProductiefUur: uren != null && uren > 0 ? rond2(naarEuro(totaalCenten) / uren) : null,
    markeringen,
  }
}

/**
 * Productieve uren = gewerkte uren EXCLUSIEF reisuren (FO-461 §C6): reisuren zijn wel
 * loonkost maar geen productieve uren, zodat de kostprijs per productief uur eerlijk is.
 */
export function productieveUrenUit(input: { gewerkteUren: number; reisuren: number; reisurenInGewerkt: boolean }): number {
  const uren = input.reisurenInGewerkt ? input.gewerkteUren - input.reisuren : input.gewerkteUren
  return rond2(Math.max(0, uren))
}
